import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { SEO } from './SEO';

export const ServicesPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: "Promotion immobilière",
      description: "Concevoir et développer des projets immobiliers sur mesure, à haute valeur architecturale et économique.",
      details: "De la première esquisse à la commercialisation, chaque projet est pensé dans sa globalité : implantation, image, qualité de vie, rentabilité et durabilité. Je coordonne les études, les partenaires et les démarches administratives jusqu'à la mise sur le marché, afin d'offrir des réalisations cohérentes, esthétiques et performantes, adaptées à la demande locale.",
      prestations: [
        "Études de marché et analyse du potentiel foncier",
        "Élaboration de concepts architecturaux et de plans directeurs",
        "Coordination des architectes, ingénieurs et communes",
        "Constitution de sociétés de projet et recherche de financements",
        "Pilotage complet jusqu'à la livraison et la commercialisation"
      ]
    },
    {
      title: "Vente & Commercialisation",
      description: "Stratégie de commercialisation sur mesure et accompagnement personnalisé pour la vente de biens immobiliers.",
      details: "Je propose une approche pour la vente de biens immobiliers, qu'il s'agisse de projets neufs, de chalets de prestige, ou d'immeubles existants. Chaque mandat est traité avec une approche rigoureuse et qualitative, visant à maximiser la visibilité, la valorisation et la rapidité de transaction.",
      prestations: [
        "Élaboration de la stratégie de vente : positionnement du bien, étude du marché, pricing et storytelling immobilier",
        "Marketing digital & traditionnel : campagnes ciblées sur les portails suisses et internationaux, création de brochures, vidéos et supports visuels professionnels",
        "Visites virtuelles 3D & outils immersifs : mise en valeur des biens via des technologies immersives (BIMx, Matterport, visites interactives, rendus photo-réalistes)",
        "Réseau d'acheteurs suisses et internationaux : diffusion sélective auprès d'investisseurs, résidents secondaires, et acheteurs qualifiés",
        "Négociation & closing : accompagnement complet jusqu'à la signature notariée, gestion des offres et sécurisation du processus de vente"
      ]
    },
    {
      title: "Achat de terrain & développement immobilier",
      description: "Identifier les meilleures opportunités foncières et transformer le potentiel d'un site en un projet concret.",
      details: "Je réalise des analyses de faisabilité technique, urbanistique et financière, garantissant une base solide pour la décision d'investissement.",
      prestations: [
        "Prospection foncière et négociation d'acquisition",
        "Études de faisabilité (COS/IBUS, gabarits, contraintes LRS/Lex Weber, etc.)",
        "Estimation des coûts, des marges et de la rentabilité",
        "Montage juridique et financier (SPV, PPE, para-hôtelier, etc.)",
        "Définition du concept global (résidentiel, touristique ou mixte)"
      ]
    },
    {
      title: "Construction & maîtrise d'ouvrage",
      description: "Je prends en charge l'ensemble du pilotage de la construction, en assurant la qualité, la conformité et la maîtrise des coûts à chaque étape.",
      details: "Grâce à une collaboration étroite avec les bureaux d'architectes, ingénieurs et entreprises, j'assure un suivi technique précis et réactif.",
      prestations: [
        "Direction de travaux et coordination des corps de métier",
        "Suivi des coûts, délais et qualité d'exécution",
        "Organisation des appels d'offres et adjudications",
        "Contrôle des situations financières et révisions budgétaires",
        "Réception des ouvrages et levée des réserves"
      ]
    },
    {
      title: "Pilotage de projets de A à Z",
      description: "Un accompagnement global pour les investisseurs, propriétaires ou institutions souhaitant déléguer entièrement la gestion de leurs projets.",
      details: "J'assure la planification, la coordination et la supervision complète des opérations, du concept initial jusqu'à la livraison clé en main.",
      prestations: [
        "Gestion administrative et technique intégrale",
        "Coordination des mandataires, entreprises et autorités",
        "Suivi permanent du planning et des coûts",
        "Reporting régulier et transparent pour les investisseurs",
        "Livraison, contrôle qualité et documentation complète de fin de projet"
      ]
    },
    {
      title: "Gestion de projets de rénovation",
      description: "La rénovation demande une expertise fine : diagnostiquer, planifier et valoriser sans dénaturer.",
      details: "J'interviens dans la modernisation, la transformation et la réhabilitation d'immeubles existants, qu'il s'agisse de valorisation patrimoniale ou d'amélioration énergétique.",
      prestations: [
        "Audit technique et énergétique de l'existant",
        "Étude de faisabilité et phasage des travaux",
        "Mise aux normes et optimisation thermique/acoustique",
        "Coordination des interventions en site occupé",
        "Valorisation post-travaux et repositionnement du bien"
      ]
    },
    {
      title: "Location immobilière",
      description: "Je valorise les biens immobiliers à travers une mise en location professionnelle et ciblée.",
      details: "Qu'il s'agisse de résidences principales, secondaires ou de locations saisonnières, l'objectif est d'optimiser l'occupation, maximiser le rendement et assurer une expérience locataire fluide.",
      prestations: [
        "Shooting photo, rédaction et diffusion des annonces",
        "Sélection et vérification des locataires (KYC, références, solvabilité)",
        "Élaboration des contrats de bail et états des lieux",
        "Gestion des réservations et accueil (courte durée)",
        "Optimisation du taux d'occupation et de la rentabilité"
      ]
    },
    {
      title: "Gérance & gestion d'immeubles",
      description: "Une gestion complète, transparente et rigoureuse, couvrant tous les aspects administratifs, techniques et financiers.",
      details: "Je veille à préserver la valeur de votre patrimoine tout en simplifiant votre quotidien.",
      prestations: [
        "Suivi des loyers, charges et assurances",
        "Gestion technique (maintenance, entretien, interventions)",
        "Relations avec les copropriétaires, locataires et fournisseurs",
        "Tenue des comptes, rapports périodiques et budgets prévisionnels",
        "Gestion proactive des améliorations et valorisation du parc immobilier"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Services | IMMO CONCEPT - Promotion & Vente Immobilière Suisse"
        description="Services complets de promotion immobilière en Suisse Romande: développement de projets, vente & commercialisation, construction, gestion et rénovation. Expertise complète du cycle immobilier."
        keywords="services immobiliers suisse, promotion immobilière, vente immobilière suisse, construction suisse, gestion immobilière, rénovation immobilière, développement immobilier suisse romande"
        url="https://real-estate-build-1.preview.emergentagent.com/services"
      />
      {/* Header avec bouton retour */}
      <div className="bg-[#1e5a7a] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-[#f5b800] mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Nos Services
          </h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Spécialisé dans l'ensemble du cycle de vie immobilier
          </p>
        </div>
      </div>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              J'interviens à chaque étape du développement immobilier, depuis l'acquisition du terrain jusqu'à la gestion locative, en assurant une approche cohérente, qualitative et durable.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>Mon objectif :</strong> créer, valoriser et pérenniser des actifs immobiliers à forte valeur ajoutée, en combinant vision stratégique, rigueur technique et maîtrise opérationnelle.
            </p>
          </div>
        </div>
      </section>

      {/* Services détaillés */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, index) => (
              <Card key={index} className="border-l-4 border-l-[#f5b800] shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#1e5a7a]">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-lg font-semibold text-gray-800">
                    {service.description}
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    {service.details}
                  </p>
                  
                  <div className="mt-6">
                    <h4 className="font-semibold text-[#1e5a7a] mb-3">
                      Prestations typiques :
                    </h4>
                    <ul className="space-y-2">
                      {service.prestations.map((prestation, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle size={20} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{prestation}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contactez-nous pour discuter de vos besoins et découvrir comment nous pouvons vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold px-8 py-6 text-lg"
            >
              Nous contacter
            </Button>
            <Button
              onClick={() => window.open('https://www.linkedin.com/company/immoconcept-rieme/', '_blank')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e5a7a] font-semibold px-8 py-6 text-lg"
            >
              Suivez-nous sur LinkedIn
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};